// header.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const lastScrollY = useRef(window.scrollY);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch contact info from Statamic API
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await fetch('/api/contact-info');
        if (!response.ok) {
          throw new Error('Failed to fetch contact info');
        }
        const data = await response.json();
        setContactInfo(data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching contact info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  const navigationItems = [
    { name: 'Home', path: '/homepage-premium-healthcare-digital-experience' },
    { name: 'About', path: '/about-our-story-medical-philosophy' },
    { name: 'Services', path: '/services-comprehensive-eye-care-universe' },
    { name: 'Our Team', path: '/our-team-expert-network-personal-connections' },
    { name: 'Our Branches', path: '/locations-community-health-hub-network' }
  ];

  const moreItems = [
    { name: 'Contact', path: '/contact-multi-channel-communication-hub' },
    { name: 'Careers', path: '/career-page' },
    { name: 'Patient Resources', path: '/patient-resources-page' },
    { name: 'FAQs', path: '/fa-qs-page' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);

      if (window.scrollY > lastScrollY.current) {
        setShowBanner(false); // scrolling down
      } else {
        setShowBanner(true); // scrolling up
      }
      lastScrollY.current = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const handleBookAppointment = () => {
    navigate('/book-appointment-guided-care-journey');
  };

  // Format phone number for WhatsApp (remove non-digit characters)
  const formatWhatsAppNumber = (number) => {
    return number ? number.replace(/\D/g, '') : '';
  };

  return (
    <>
      {/* Emergency Banner */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.4, 0.0, 0.2, 1] }}
            className="fixed top-0 left-0 right-0 z-40 bg-primary text-white py-2 px-4"
          >
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center space-x-3">
                { contactInfo ? (
                  <a
                    href={`mailto:${contactInfo.email_address}`}
                    className="flex items-center space-x-2 font-body text-sm font-medium"
                  >
                    <Icon name="Mail" size={20} className="animate-pulse" />
                    <span>{contactInfo.email_address}</span>
                  </a>
                ) : null}
              </div>
              <div className="flex items-center space-x-2">
                {contactInfo ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.location.href = `tel:${contactInfo.phone_number}`}
                      iconName="Phone"
                      iconPosition="left"
                      className="text-white hover:bg-white/20 text-xs"
                    >
                      Call Now
                    </Button>
                    {/* <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`https://wa.me/${formatWhatsAppNumber(contactInfo.whatsapp_number)}?text=Hi%20there!%20%F0%9F%91%8B%20Welcome%20to%20Eye%26U%20Kenya.`, '_blank')}
                      iconName="MessageCircle"
                      iconPosition="left"
                      className="text-white hover:bg-white/20 text-xs"
                    >
                      WhatsApp
                    </Button> */}
                  </>
                ) : null}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm medical-shadow' : 'bg-white'
      }`} style={{ top: showBanner ? '48px' : '0px' }}>
        <div className="w-full">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            {/* Logo */}
            <Link 
              to="/homepage-premium-healthcare-digital-experience" 
              className="flex items-center space-x-4 hover:opacity-80 medical-transition"
              onClick={closeMobileMenu}
            >
              <img
                  src="/storage/logo_icon/eye-u-logo.png"
                  alt="Eye&U Logo"
                  className="w-20 h-20 object-contain rounded-lg"
              />
              <div className="flex flex-col">
                <span className="font-heading font-bold text-xl text-text-primary leading-none">
                  Eye&U Ophthalmics
                </span>
                <span className="font-sans text-lg text-text-secondary leading-snug">
                  For Your Eyes Only
                </span>


              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`font-heading font-medium text-sm medical-transition hover:text-primary ${
                    isActivePath(item.path) 
                      ? 'text-primary border-b-2 border-primary pb-1' :'text-text-primary'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* More Dropdown */}
              <div className="relative group">
                <button className="flex items-center space-x-1 font-heading font-medium text-sm text-text-primary hover:text-primary medical-transition">
                  <span>More</span>
                  <Icon name="ChevronDown" size={16} />
                </button>
                
                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg medical-shadow border opacity-0 invisible group-hover:opacity-100 group-hover:visible medical-transition">
                  <div className="py-2">
                    {moreItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block px-4 py-2 text-sm font-medium medical-transition hover:bg-muted ${
                          isActivePath(item.path) ? 'text-primary bg-muted' : 'text-text-primary'
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </nav>

            {/* Desktop CTA Button */}
            <div className="hidden lg:flex items-center space-x-3">
              <Button 
                variant="default" 
                size="sm"
                iconName="Calendar"
                iconPosition="left"
                iconSize={16}
                onClick={handleBookAppointment}
              >
                Book Appointment
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-lg hover:bg-muted medical-transition"
              aria-label="Toggle mobile menu"
            >
              <Icon 
                name={isMobileMenuOpen ? "X" : "Menu"} 
                size={24} 
                color="var(--color-text-primary)" 
              />
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`lg:hidden overflow-hidden medical-transition ${
            isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="bg-white border-t px-4 py-4 space-y-4">
              {/* Mobile Navigation Links */}
              <nav className="space-y-3">
                {navigationItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={closeMobileMenu}
                    className={`block py-2 font-heading font-medium medical-transition ${
                      isActivePath(item.path) 
                        ? 'text-primary border-l-4 border-primary pl-4' :'text-text-primary hover:text-primary hover:pl-2'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                {moreItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={closeMobileMenu}
                    className={`block py-2 font-heading font-medium medical-transition ${
                      isActivePath(item.path) 
                        ? 'text-primary border-l-4 border-primary pl-4' :'text-text-primary hover:text-primary hover:pl-2'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>

              {/* Mobile CTA Button */}
              <div className="pt-4 border-t space-y-3">
                <Button 
                  variant="default" 
                  fullWidth
                  iconName="Calendar"
                  iconPosition="left"
                  iconSize={16}
                  onClick={handleBookAppointment}
                >
                  Book Appointment
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;